import React, { useState } from "react";
import type { ToastType } from "../types/Toast";

import "./Toast.css";

interface Props {
    text: string;
    type: ToastType;
    duration: number;
    parseText: boolean;
    onExpire: () => void;
}

function parseColorTags(text: string): (string | React.JSX.Element)[] {
    const regex = /\[color:(#[0-9a-fA-F]{3,6})\](.*?)\[\/color\]/g;
    const parts: (string | React.JSX.Element)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        parts.push(
            <span key={`color-${match.index}`} style={{ color: match[1] }}>
                {match[2]}
            </span>,
        );
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) parts.push(text.substring(lastIndex));
    return parts.length > 0 ? parts : [text];
}

function parseTextLinks(str: string) {
    const regex = /(https?:\/\/[^\s^"<>]+[^\s^"<>.,:;?!])/g;

    // Split the string by the regex, but keep the matches in the array
    const parts = str.split(regex);

    return parts.map((part, index) => {
        // If the part matches our URL pattern, wrap it in a div/span
        if (part.match(regex)) {
            return (
                <span key={index} className="toast-link">
                    {part}
                </span>
            );
        }
        // Otherwise, return the plain text
        return part;
    });
}

export function formatToastMessage(
    message: string,
): (string | React.JSX.Element)[] {
    // Pass 1: Handle Colors
    const colorPass = parseColorTags(message);

    // Pass 2: Handle Links by looking at the string parts left over from Pass 1
    return colorPass.flatMap((part, _) => {
        if (typeof part === "string") {
            return parseTextLinks(part);
        }
        // If it's already a JSX Element (from the color pass), leave it alone
        return part;
    });
}

export function Toast({ text, type, duration, parseText, onExpire }: Props) {
    const [hide, setHide] = useState(false);

    const handleAnimationEnd = (ev: React.AnimationEvent) => {
        if (ev.animationName === "show-toast")
            setTimeout(() => setHide(true), duration);
        else onExpire();
    };

    return (
        <div
            className={`toast-text ${hide ? "hide" : "show"} ${type}`}
            onAnimationEnd={handleAnimationEnd}
        >
            {parseText ? formatToastMessage(text) : text}
        </div>
    );
}
